import { useCallback, useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { LO_SITE_STRUCTURED_TEXT_EDITABLE_SELECTORS } from '@/lib/lyrraone-structured-text';
import { cn } from '@/lib/utils';

interface LoSiteStructuredTextViewProps {
  body: string;
  editMode?: boolean;
  selected?: boolean;
  className?: string;
  style?: CSSProperties;
  onSelect?: () => void;
  onChange?: (html: string) => void;
}

export function LoSiteStructuredTextView({
  body,
  editMode,
  selected,
  className,
  style,
  onSelect,
  onChange,
}: LoSiteStructuredTextViewProps) {
  const rootRef = useRef<HTMLElement>(null);
  const lastBodyRef = useRef<string | null>(null);

  const syncHtml = useCallback(() => {
    if (!rootRef.current || !onChange) return;
    const html = rootRef.current.innerHTML;
    lastBodyRef.current = html;
    onChange(html);
  }, [onChange]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || body === lastBodyRef.current) return;
    if (root.querySelector('[contenteditable="true"]')) return;
    root.innerHTML = body;
    lastBodyRef.current = body;
  }, [body]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !editMode) return;

    const elements = root.querySelectorAll(LO_SITE_STRUCTURED_TEXT_EDITABLE_SELECTORS);
    const cleanups: Array<() => void> = [];

    elements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.classList.add('lo-site-structured-text-editable');

      const beginEdit = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
        onSelect?.();
        if (htmlEl.contentEditable === 'true') return;
        htmlEl.contentEditable = 'true';
        htmlEl.focus();
      };

      const onClick = (e: Event) => beginEdit(e);

      const onDblClick = (e: Event) => beginEdit(e);

      const onBlur = () => {
        if (htmlEl.contentEditable !== 'true') return;
        htmlEl.contentEditable = 'false';
        syncHtml();
      };

      const onPaste = (e: ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData?.getData('text/plain') ?? '';
        document.execCommand('insertText', false, text);
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          htmlEl.contentEditable = 'false';
          htmlEl.blur();
          return;
        }
        const allowMultiline = htmlEl.tagName === 'P' || htmlEl.matches('blockquote');
        if (e.key === 'Enter' && !e.shiftKey && !allowMultiline) {
          e.preventDefault();
          htmlEl.blur();
        }
      };

      htmlEl.addEventListener('click', onClick);
      htmlEl.addEventListener('dblclick', onDblClick);
      htmlEl.addEventListener('blur', onBlur);
      htmlEl.addEventListener('paste', onPaste);
      htmlEl.addEventListener('keydown', onKeyDown);

      cleanups.push(() => {
        htmlEl.removeEventListener('click', onClick);
        htmlEl.removeEventListener('dblclick', onDblClick);
        htmlEl.removeEventListener('blur', onBlur);
        htmlEl.removeEventListener('paste', onPaste);
        htmlEl.removeEventListener('keydown', onKeyDown);
        htmlEl.classList.remove('lo-site-structured-text-editable');
        htmlEl.contentEditable = 'false';
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [editMode, body, syncHtml, onSelect]);

  return (
    <section
      ref={rootRef as never}
      className={cn(
        'lo-site-rich-section eduflow-rich-html',
        editMode && 'lo-site-editable lo-site-structured-text-root',
        selected && 'lo-site-element-selected',
        className,
      )}
      style={style}
      onClick={(e) => {
        if (!editMode) return;
        e.stopPropagation();
        onSelect?.();
      }}
      suppressContentEditableWarning
    />
  );
}
